import classNames from 'classnames'
import type { ReactElement } from 'react'
import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import type {
  Placement,
  ReferenceType,
  UseHoverProps,
} from '@floating-ui/react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useInteractions,
  FloatingPortal,
  useTransitionStyles,
  arrow,
  FloatingArrow,
  useRole,
  useClick,
  FloatingFocusManager,
  safePolygon,
} from '@floating-ui/react'
import { useTranslationTyped } from 'i18n/i18n'

const theme = {
  tooltip: {
    base: {
      className:
        'bg-on-surface-var text-xs text-core-tertiary p-2 rounded-lg cursor-default relative z-9999 min-w-[30px] max-w-[300px] text-center',
    },
    transition: {
      duration: 300,
    },
  },
}

// eslint-disable-next-line react-refresh/only-export-components
export function hideAllTooltips() {
  const event = new KeyboardEvent('keydown', {
    key: 'Escape',
  })
  document.dispatchEvent(event)
}

export type BreakoutTooltipProps = {
  enabled?: boolean
  cloneRef?: (ref: ReferenceType) => void
  className?: string
  content:
    | ReactElement
    | string
    | (({ close }: { close: () => void }) => ReactElement | string)
  children: ReactElement | string
  placement?: Placement
  tooltipClassNameOverrides?: string
  delay?: UseHoverProps['delay']
  // Whether to render the tooltip in a portal or not - defaults to true
  portal?: boolean
  portalId?: string
  ariaHidden?: boolean
  forceOpen?: boolean
  transitionDuration?:
    | number
    | {
        open?: number
        close?: number
      }
  treatReactElementAsString?: boolean
  /** The 'popover' behavior shows the content on click and keeps it open after the mouse moves away. */
  behavior?: 'tooltip' | 'popover'
  /**
   * Set when the child is itself a button, so a popover can expose its state
   * with aria-expanded/aria-controls. Popovers wrapping a plain <div> must
   * leave this unset - aria-expanded is not an allowed attribute there.
   */
  triggerIsButton?: boolean
  /**
   * Let a popover also open on mouse hover, on top of click activation. The
   * hover-opened popover behaves as a plain tooltip and does not take focus.
   */
  openOnHover?: boolean
  /** ARIA role for the floating element. Use 'alert' for urgent notifications that screen readers should announce immediately. */
  role?: 'tooltip' | 'alert'
}

export function BreakoutTooltip({
  content,
  children,
  cloneRef,
  placement,
  delay,
  portal = true,
  portalId,
  className,
  transitionDuration,
  tooltipClassNameOverrides,
  enabled = true,
  forceOpen = false,
  ariaHidden = false,
  treatReactElementAsString = false,
  behavior = 'tooltip',
  role: roleProp = 'tooltip',
  triggerIsButton = false,
  openOnHover = false,
}: BreakoutTooltipProps) {
  const { tt } = useTranslationTyped()
  const [isOpen, setIsOpen] = useState(false)
  // A popover that the mouse opened is a passive tooltip, not a manually
  // activated one, so it must not pull focus out from under the pointer.
  const [openedByHover, setOpenedByHover] = useState(false)
  const tooltipPlacement = placement || 'bottom'

  const arrowRef = useRef(null)

  const id = useId()
  const { refs, floatingStyles, context } = useFloating({
    open: forceOpen || isOpen,
    onOpenChange: (open, _event, reason) => {
      setIsOpen(open)
      setOpenedByHover(open && reason === 'hover')
    },
    placement: tooltipPlacement,
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  })

  const role = useRole(context, { role: 'tooltip' })

  useEffect(() => {
    // If the tooltip is disabled, reset the open state to closed
    // That prevents it from showing up open when it's enabled again
    if (enabled === false) setIsOpen(false)
  }, [enabled])

  const { isMounted, styles } = useTransitionStyles(context, {
    // Configure both open and close durations:
    duration:
      behavior === 'popover'
        ? 0
        : (transitionDuration ?? theme.tooltip.transition.duration),
  })

  // Event listeners to change the open state
  const hover = useHover(context, {
    move: false,
    delay,
    enabled: behavior === 'tooltip' || openOnHover,
    // Touch and pen emulate hover, and a popover reached that way should go
    // through click activation instead so it stays open.
    mouseOnly: behavior === 'popover',
    // The content sits 10px away from the trigger, so keep it open while the
    // pointer travels across that gap - WCAG 1.4.13 asks for hoverable content.
    handleClose: openOnHover ? safePolygon() : undefined,
  })
  const focus = useFocus(context, {
    enabled: behavior === 'tooltip',
  })
  const click = useClick(context, {
    enabled: behavior === 'popover',
  })
  const dismiss = useDismiss(context, {
    ancestorScroll: true,
  })

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ])

  const tooltipClassName = theme.tooltip.base.className

  const close = useCallback(() => setIsOpen(false), [])

  // A manually-activated tooltip has to expose its state on the trigger, but
  // aria-expanded is only valid on something the user can activate, so the
  // caller declares that rather than us guessing from the element type.
  const manualActivationProps =
    behavior === 'popover' && triggerIsButton
      ? {
          'aria-expanded': forceOpen || isOpen,
          'aria-controls': id,
        }
      : {}

  const resolvedContent =
    typeof content === 'function' ? content({ close }) : content

  let contentIsReactElement = typeof resolvedContent !== 'string'

  if (treatReactElementAsString && contentIsReactElement) {
    contentIsReactElement = false
  }

  const floatingContent = (
    <div
      aria-hidden={ariaHidden}
      id={id}
      className={classNames(
        className,
        {
          [tooltipClassName]: !contentIsReactElement,
          'relative z-9999 cursor-default': contentIsReactElement,
        },
        tooltipClassNameOverrides
      )}
      ref={refs.setFloating}
      style={{ ...floatingStyles, ...styles }}
      {...getFloatingProps()}
      role={roleProp}
    >
      {!contentIsReactElement && (
        <FloatingArrow
          className="fill-on-surface-var"
          ref={arrowRef}
          context={context}
        />
      )}
      {resolvedContent}
    </div>
  )

  const component =
    isMounted &&
    (behavior === 'popover' && !openedByHover ? (
      <FloatingFocusManager
        context={context}
        restoreFocus={true}
        // Focus is modal here, so without a close button there is no way out
        // for touch-based screen reader users, who have no escape key.
        visuallyHiddenDismiss={tt.common.close()}
      >
        {floatingContent}
      </FloatingFocusManager>
    ) : (
      floatingContent
    ))

  if (!enabled) {
    return children
  }

  // references:
  // - https://hub.accessible360.com/kb/articles/524#heading-for-automaticallyactivated-tooltips
  // - https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/tooltip_role
  // Popover triggers expose their open state, but not aria-haspopup: the panel
  // they open is a role="tooltip", and there is no aria-haspopup value for a
  // tooltip. Claiming "dialog" would promise a widget that never arrives.
  const popoverReferenceProps =
    behavior === 'popover'
      ? {
          'aria-expanded': forceOpen || isOpen,
        }
      : {}

  return (
    <>
      {typeof children === 'string' ? (
        // Plain text has no focusable element of its own, so wrap it in a
        // button: keyboard users can then tab to it to open the tooltip.
        <button
          type="button"
          className={classNames({ 'cursor-default': behavior === 'tooltip' })}
          aria-describedby={forceOpen || isOpen ? id : undefined}
          ref={refs.setReference}
          {...getReferenceProps()}
          {...popoverReferenceProps}
        >
          {children}
        </button>
      ) : (
        cloneElement(children, {
          ref: (node: ReferenceType) => {
            refs.setReference(node)
            cloneRef?.(node)
          },
          ...getReferenceProps({}),
          ...popoverReferenceProps,
          'aria-describedby': mergeDescribedBy(
            children.props['aria-describedby'],
            forceOpen || isOpen ? id : undefined
          ),
          ...manualActivationProps,
        })
      )}
      {(forceOpen || isOpen) &&
        (portal ? (
          <FloatingPortal id={portalId}>{component}</FloatingPortal>
        ) : (
          component
        ))}
    </>
  )
}

// Keep any description the child already has (e.g. a disabled button's
// reason) instead of replacing it with the tooltip's id.
function mergeDescribedBy(existing: unknown, tooltipId: string | undefined) {
  const ids = [typeof existing === 'string' ? existing : undefined, tooltipId]
    .filter(Boolean)
    .join(' ')
  return ids || undefined
}
